import { ulid } from 'ulid'
import { useCallback, useReducer, useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import Card from '@/components/common/Card'
import DropZone from './DropZone'
import FilesList from './FilesList'
import { useSubscription } from '@apollo/client'
import { showNotification } from '@/utils/notifications'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import { adAssetUploadReducer, initialState, ACTIONS, FILE_STATES } from '@/reducers/adAssetUploadReducer'
import {
    UPLOAD_FILES,
    DELETE_AD_ASSET,
    AD_ASSET_UPLOAD_SUBSCRIPTION,
    CANCEL_UPLOAD,
    GET_AD_ASSETS,
} from '@/graphql/ads/ad-assets-queries'
import { CubeLoader } from '@/components/ui/cube-loader'

const Upload = ({
    campaignId,
    adId,
    fetchAdId,
    allowMultiple,
    campaignFormat,
    registerNextGuard,
    deregisterNextGuard,
    setStepContentData,
}) => {
    const [state, dispatch] = useReducer(adAssetUploadReducer, initialState)
    const { uploadId, files } = state
    const [uploadFiles] = useMutation(UPLOAD_FILES)
    const [deleteAdAsset] = useMutation(DELETE_AD_ASSET)
    const [cancelUpload] = useMutation(CANCEL_UPLOAD)
    const [formState, setFormState] = useState('initial')
    const [adAssets, setAdAssets] = useState([])
    const [adAssetsLoading, setAdAssetsLoading] = useState(true)
    const { getStepContentData, removeStepContentData, goToStep, markStepAs, cloneCreativeId } = useWizard()
    const [replacingFormat, setReplacingFormat] = useState([])

    useQuery(GET_AD_ASSETS, {
        variables: { campaignId, adId: fetchAdId ?? adId, hasMetaId: false, source: 'upload' },
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            setAdAssets(data.AdAsset?.adAssets)
            setAdAssetsLoading(false)
        },
    })

    useEffect(() => {
        const replacingFormat = getStepContentData('uploadAssets')?.replaceFormat
        if (replacingFormat) setReplacingFormat(replacingFormat)
    }, [])

    useEffect(() => {
        const isUploading = files.some((file) => file.state === FILE_STATES.UPLOADING)
        const hasFilesToUpload = files.some((file) => file.isNew && file.state === FILE_STATES.INITIAL)
        const allFilesUploaded = files.every((file) => file.state === FILE_STATES.UPLOADED)

        if (isUploading) setFormState('uploading')
        else if (hasFilesToUpload) setFormState('dirty')
        else if (allFilesUploaded) setFormState('clean')
        else setFormState('initial')
    }, [files])

    useEffect(() => {
        adAssets.forEach((asset) => {
            const payload = {
                id: asset.id,
                isNew: false,
                name: asset.name,
                uploadedKB: asset.size / 1024,
                totalKB: asset.size / 1024,
                width: asset.width,
                height: asset.height,
                thumbnails: asset.thumbnails,
                url: asset.url,
                state: FILE_STATES.UPLOADED,
                type: asset.type,
            }

            dispatch({ type: ACTIONS.ADD_FILE, payload })
        })
    }, [adAssets])

    const validate = (uploadedFiles, uploadingFiles, campaignFormat, uploadedPhotos, uploadedVideos) => {
        if (uploadingFiles.length > 0) return 'Please wait for the current upload to complete'
        if (uploadedFiles.length === 0) return 'Please upload at least one media file'
        if (campaignFormat === 'carousel' && uploadedPhotos.length === 0 && uploadedVideos.length === 0)
            return 'Please select at least one media file'

        if (campaignFormat === 'video' && uploadedVideos.length === 0) return 'Please upload video to continue'
        if (campaignFormat === 'singleImage' && uploadedPhotos.length === 0) return 'Please upload image to continue'

        return null
    }

    useEffect(() => {
        const guard = registerNextGuard(() => {
            const uploadedFiles = files.filter((file) => file.state === FILE_STATES.UPLOADED)
            const uploadingFiles = files.filter((file) => file.state === FILE_STATES.UPLOADING)
            const uploadedVideos = uploadedFiles.filter((asset) => asset.type === 'video')
            const uploadedPhotos = uploadedFiles.filter((asset) => asset.type === 'image')
            const errorMessage = validate(uploadedFiles, uploadingFiles, campaignFormat, uploadedPhotos, uploadedVideos)
            const scrollToTop =
                (campaignFormat === 'carousel' && uploadedPhotos.length === 0 && uploadedVideos.length === 0) ||
                (campaignFormat === 'video' && uploadedVideos.length === 0) ||
                (campaignFormat === 'singleImage' && uploadedPhotos.length === 0)

            if (errorMessage) {
                if (scrollToTop) window.scrollTo({ top: 0, behavior: 'smooth' })
                showNotification(errorMessage, 'error', 3000, null, null, false, true)
                return false
            }

            if (campaignFormat === 'video') {
                setStepContentData({ assetId: uploadedFiles[0].id }, 'videoTrim')
            } else {
                setStepContentData({ assetId: uploadedFiles[0].id }, 'setMediaCrop')
            }

            if (cloneCreativeId && uploadedFiles.some((f) => f.isNew)) {
                setStepContentData({ cloneWithNewAssets: true }, 'uploadAssets')
            }

            markStepAs(1)
            goToStep(calculateNextStep())

            return false
        })

        return () => deregisterNextGuard(guard)
    }, [files])

    const calculateNextStep = () => {
        if (campaignFormat === 'carousel') return 'carouselCards'
        else if (campaignFormat === 'video') return 'videoTrim'

        return 'setMediaCrop'
    }

    useSubscription(AD_ASSET_UPLOAD_SUBSCRIPTION, {
        variables: { uploadId },
        skip: !uploadId,

        onData: ({ data }) => {
            const fileData = data.data.adAssetUpload
            dispatch({ type: ACTIONS.UPDATE_FILE, payload: fileData })

            if (fileData.newId)
                dispatch({ type: ACTIONS.UPDATE_ID, payload: { oldId: fileData.fileId, newId: fileData.newId } })
        },
    })

    const onDrop = useCallback(
        (acceptedFiles) => {
            if (acceptedFiles.length === 0) return

            if (campaignFormat === 'carousel') {
                // Allow up to 10 cards
                if (files.length + acceptedFiles.length > 10) {
                    showNotification('You can only upload up to 10 cards', 'error', 3000, null, null, false, true)
                    acceptedFiles = acceptedFiles.slice(0, 10 - files.length)
                }
            } else {
                // Remove previous files if single image or video
                files.forEach((file) => {
                    if (file.state === FILE_STATES.INITIAL) {
                        dispatch({ type: ACTIONS.REMOVE_FILE, payload: { fileId: file.id } })
                    }
                })
            }

            let replaceFormat = null
            if (campaignFormat === 'video' || campaignFormat === 'singleImage') {
                replaceFormat = setStepContentData('replaceFormat')
            }

            acceptedFiles.forEach((file) => {
                const payload = {
                    id: ulid(),
                    isNew: true,
                    name: file.name,
                    size: file.size,
                    state: FILE_STATES.INITIAL,
                    uploadedKB: 0,
                    totalKB: (file.size / 1024).toFixed(2),
                    type: file.type.startsWith('image') ? 'image' : 'video',
                    replaceFormat,
                    file,
                }

                dispatch({ type: ACTIONS.ADD_FILE, payload })
            })
        },
        [files],
    )

    const removeUploadedFiles = () => {
        files
            .filter((file) => file.state === FILE_STATES.UPLOADED)
            .forEach((file) => dispatch({ type: ACTIONS.REMOVE_FILE, payload: { fileId: file.id } }))
    }

    //  If image and zero replacingFormat

    const handleUpload = async () => {
        // const replaceExisting = !allowMultiple && replacingFormat.length !== 0

        if (!allowMultiple && replacingFormat.length === 0) removeUploadedFiles()
        if (replacingFormat.length) removeStepContentData('uploadAssets', 'replaceFormat')

        const uploadFilesData = files
            .filter((file) => file.isNew && file.state === FILE_STATES.INITIAL)
            .map((fileObj) => {
                dispatch({ type: ACTIONS.UPDATE_FILE, payload: { fileId: fileObj.id, state: FILE_STATES.UPLOADING } })

                return {
                    uuid: fileObj.id,
                    file: fileObj.file,
                }
            })

        uploadFiles({
            variables: {
                files: uploadFilesData,
                uploadId,
                campaignId,
                adId,
                replaceExisting: false, // TODO: remove this flag and let backend handle it
                allowMultiple,
                source: 'upload',
                formats: campaignFormat === 'carousel' ? [] : replacingFormat,
            },
        })

        setStepContentData({}, 'uploadAssets')
    }

    const removeFile = async (fileId) => {
        const file = files.find((f) => f.id === fileId)

        if (file.state === FILE_STATES.UPLOADED) deleteAdAsset({ variables: { id: fileId } })

        dispatch({ type: ACTIONS.REMOVE_FILE, payload: { fileId } })
    }

    const handleCancelAll = async () => {
        const ids = files.filter((file) => file.state === FILE_STATES.UPLOADING).map((file) => file.id)
        await cancelUpload({ variables: { ids } })

        ids.forEach((id) => {
            dispatch({ type: ACTIONS.REMOVE_FILE, payload: { fileId: id } })
        })
    }

    const handleCancel = async (fileId) => {
        await cancelUpload({ variables: { ids: [fileId] } })
        dispatch({ type: ACTIONS.REMOVE_FILE, payload: { fileId } })
    }

    if (adAssetsLoading)
        return (
            <div className="tw:w-full tw:flex tw:items-center tw:justify-center">
                <CubeLoader size="default" fullScreen={true} />
            </div>
        )

    return (
        <Card className="border-0 d-flex flex-column gap-3" style={{ backgroundColor: 'transparent' }}>
            <div className="upload-container" style={{ borderRadius: 2, backgroundColor: '#17273C' }}>
                <DropZone onDrop={onDrop} allowMultiple={allowMultiple} campaignFormat={campaignFormat} />
            </div>
            <FilesList
                files={files}
                state={formState}
                removeFile={removeFile}
                handleUpload={handleUpload}
                handleCancelAll={handleCancelAll}
                handleCancel={handleCancel}
            />
        </Card>
    )
}

export default Upload
